import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Chip,
    Avatar,
    Collapse
} from '@mui/material';

interface CommentProps {
    comment: any;
    depth: number;
    onReply: (id: number) => void;
    onEdit: (comment: any) => void;
    renderAttachedFiles: (files: string[]) => JSX.Element;
    renderComment: (comment: any, depth: number) => JSX.Element;
}

const CommentItem: React.FC<CommentProps> = ({
    comment,
    depth,
    onReply,
    onEdit,
    renderAttachedFiles,
    renderComment
}) => {
    const isSupport = comment.support_staff;
    const canReply = depth < 1;

    return (
        <Box key={comment.id_comment} ml={depth * 4} mb={2}>
            <Box display="flex" alignItems="flex-start">
                {/* Avatar a la izquierda para todos */}
                <Avatar sx={{ bgcolor: isSupport ? '#F3F4F6' : '#E0E7FF', color: '#4F46E5', mr: 1 }}>
                    {comment.user_name?.charAt(0).toUpperCase() || "U"}
                </Avatar>

                {/* Contenido del comentario */}
                <Box flex={1}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography fontWeight={600}>{comment.user_name}</Typography>
                            <Chip
                                label={isSupport ? "Soporte" : "Usuario"}
                                size="small"
                                sx={{
                                    fontSize: '0.7rem',
                                    bgcolor: isSupport ? '#DBEAFE' : '#E0E7FF',
                                    color: isSupport ? '#1D4ED8' : '#3730A3'
                                }}
                            />
                        </Box>

                        <Box display="flex" alignItems="center" gap={2}>
                            {canReply && (
                                <Typography
                                    variant="body2"
                                    sx={{ cursor: 'pointer', color: '#6B7280' }}
                                    onClick={() => onReply(comment.id_comment)}
                                >
                                    💬 Responder
                                </Typography>
                            )}
                            <Typography
                                variant="body2"
                                sx={{ cursor: 'pointer', color: '#6B7280' }}
                                onClick={() => onEdit(comment)}
                            >
                                ✏️ Editar
                            </Typography>
                        </Box>
                    </Box>

                    <Paper
                        sx={{
                            mt: 1,
                            p: 2,
                            bgcolor: isSupport ? '#E0F2FE' : '#F3E8FF',
                            borderRadius: 2,
                            maxWidth: '100%',
                            minWidth: '450px',
                            '& p': { margin: 0, marginBottom: '0.5em' }, // estilos para <p>
                        }}
                    >
                        <Box
                            component="div"
                            dangerouslySetInnerHTML={{ __html: comment.comment }}
                        />
                        <Typography
                            variant="caption"
                            display="block"
                            align="right"
                            color="text.secondary"
                            mt={1}
                        >
                            {new Date(comment.date).toLocaleString()}
                        </Typography>
                    </Paper>

                    {comment.files?.length > 0 && renderAttachedFiles(comment.files)}
                </Box>
            </Box>

            <Collapse in={true} timeout="auto" unmountOnExit>
                <Box mt={1}>
                    {Array.isArray(comment.responses) &&
                        comment.responses.map((reply: any) =>
                            renderComment(reply, depth + 1)
                        )}
                </Box>
            </Collapse>
        </Box>
    );
};

export default React.memo(CommentItem);
